import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AccessData } from '@/app/(admin)/config/users/userAccessDefaults'
import { appBasePath } from '@/utils/paths'

const authStorageKey =
  process.env.NEXT_PUBLIC_APP_ENV === 'staging'
    ? 'auth-storage-staging'
    : appBasePath
      ? `auth-storage${appBasePath.replace(/\//g, '-')}`
      : 'auth-storage'

interface ImpersonationBackup {
  token: string
  user: AuthUser
}

export interface AppSettings {
  units?: string[]
  default_unit?: string
  currency_symbol?: string
  currency_code?: string
  decimal_separator?: '.' | ','
  thousands_separator?: ',' | '.' | ' ' | ''
  [key: string]: unknown
}

export interface AuthUser {
  _id?: string
  role?: string
  firstName?: string
  lastName?: string
  userName?: string
  created_by?: string
  impersonatedBy?: string
  showProductPrice?: boolean
  access?: AccessData
  [key: string]: unknown
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  settings: AppSettings | null
  impersonationBackup: ImpersonationBackup | null
  setAuth: (token: string, user: AuthUser) => void
  setUser: (user: AuthUser) => void
  setSettings: (settings: AppSettings | null) => void
  startImpersonation: (token: string, user: AuthUser) => void
  endImpersonation: (token: string, user: AuthUser) => void
  logout: () => void
}

export const useAuthStore = create(
  persist<AuthState>(
    (set, get) => ({
      token: null,
      user: null,
      settings: null,
      impersonationBackup: null,
      setAuth: (token, user) => set({ token, user, impersonationBackup: null }),
      setUser: (user) =>
        set((state) => {
          const impersonatedBy =
            (user as { impersonatedBy?: string }).impersonatedBy ??
            (state.user as { impersonatedBy?: string } | null)?.impersonatedBy
          return {
            user: impersonatedBy ? { ...user, impersonatedBy } : user,
          }
        }),
      setSettings: (settings) => set({ settings }),
      startImpersonation: (token, user) => {
        const state = get()
        const role = String(state.user?.role ?? '').toLowerCase()
        const backup =
          role === 'superadmin' && !state.user?.impersonatedBy && state.token
            ? { token: state.token, user: state.user }
            : state.impersonationBackup
        set({ token, user, impersonationBackup: backup })
      },
      endImpersonation: (token, user) => set({ token, user, impersonationBackup: null }),
      logout: () => set({ token: null, user: null, settings: null, impersonationBackup: null }),
    }),
    {
      name: authStorageKey,
    }
  )
)
